import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { usePlatformConfig } from "@/lib/config";
import { trackFunnel } from "@/lib/analytics";
import { useOnboardingStore } from "@/stores/onboarding";

export const Route = createFileRoute("/onboarding/consent")({
  component: ConsentStep,
});

type ConsentKey = "place_storage" | "alerts" | "live_location_while_open" | "analytics" | "ai";

const CONSENT_ROWS: Array<{
  key: ConsentKey;
  label: string;
  required: boolean;
  hint?: string;
}> = [
  {
    key: "place_storage",
    label: "Store my saved places (ward / county)",
    required: true,
    hint: "Required so flood risk matches your area.",
  },
  {
    key: "alerts",
    label: "Alerts by SMS / WhatsApp / email / push",
    required: true,
  },
  {
    key: "live_location_while_open",
    label: "Use my live location while the app is open",
    required: false,
    hint: "Web updates only while this app is open",
  },
  {
    key: "analytics",
    label: "Anonymous usage analytics",
    required: false,
  },
  {
    key: "ai",
    label: "AI-written summaries (non-identifying data only)",
    required: false,
  },
];

function ConsentStep() {
  const navigate = useNavigate();
  const { platformName } = usePlatformConfig();
  const consent = useOnboardingStore((s) => s.consent);
  const setConsent = useOnboardingStore((s) => s.setConsent);

  // Captured once on mount so it never changes on re-render.
  // Will be replaced by the server-stored timestamp in the full Change 3 implementation.
  const [consentRecordTime] = useState(() => new Date().toLocaleString());

  const requiredOk = Boolean(consent["place_storage"] && consent["alerts"]);

  return (
    <div className="flex flex-1 flex-col">
      <h1 className="font-display text-3xl font-bold">Consent & privacy</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Under the Kenya Data Protection Act 2019. Only required items are pre-selected.
      </p>

      <div className="mt-6 space-y-3">
        {CONSENT_ROWS.map(({ key, label, required, hint }) => (
          <div
            key={key}
            className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-3"
          >
            <div>
              <Label className="text-sm">{label}</Label>
              {required ? (
                <p className="mt-1 text-[10px] font-semibold uppercase text-muted-foreground">
                  Required for service
                </p>
              ) : null}
              {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
            </div>
            <Switch
              checked={Boolean(consent[key])}
              disabled={required}
              onCheckedChange={(v) => setConsent({ ...consent, [key]: v })}
            />
          </div>
        ))}
      </div>

      <div className="mt-5 rounded-md bg-muted p-3 text-xs leading-relaxed text-muted-foreground">
        <p>
          Data controller: <strong>{platformName}</strong>
        </p>
        <p className="mt-2">
          SMS Policy: alerts for your saved places are platform advisories, not official
          government warnings. You can change channels or stop SMS anytime in Settings. The full
          notice is the{" "}
          <Link to="/privacy" className="underline">
            privacy policy
          </Link>
          .
        </p>
        <p className="mt-2">
          Consent record time: {consentRecordTime} — you can{" "}
          <Link to="/app" className="underline">
            download or delete your data
          </Link>{" "}
          later in Settings.
        </p>
      </div>

      <div className="mt-auto space-y-2 pt-8">
        <Button
          className="w-full"
          disabled={!requiredOk}
          onClick={() => {
            trackFunnel("step_completed", "consent");
            try {
              window.localStorage.setItem(
                "anga_live_location_while_open",
                consent["live_location_while_open"] ? "1" : "0",
              );
            } catch {
              /* ignore */
            }
            void navigate({ to: "/onboarding/done" });
          }}
        >
          Continue
        </Button>
        <Button
          variant="outline"
          className="w-full"
          onClick={() => void navigate({ to: "/onboarding/alerts" })}
        >
          Back
        </Button>
      </div>
    </div>
  );
}
